/**
 * dateRangeParams.js — Iter 341 (Feb 2026)
 *
 * Single source of truth for translating the CRM's UI date-filter state
 * (`range` preset + optional custom from/to) into the query parameters
 * the backend rep-activity endpoints understand (`days` OR
 * `date_from`+`date_to`).
 *
 * Why this exists:
 *   Before Iter 341, three drill-down surfaces each rolled their own
 *   version of this translation — two of them were broken (Reports.jsx
 *   hardcoded days=30, AdminRepActivity.jsx::toggleAgentRow ignored
 *   range entirely). The summary counts above the drill-down always
 *   used the correct filter, so the summary and detail diverged.
 *   Iter 183 (Lisa fix, AdminRepActivity Snapshot panel) already ships
 *   the correct math; this utility just extracts it verbatim so every
 *   surface can call the same function.
 *
 * Contract:
 *   Given the UI state, return { date_from, date_to } for explicit
 *   windows (today / yesterday / custom) or { days } for numeric
 *   presets (7 / 30 / 90). Callers spread the result directly into
 *   their `params` object.
 */

/**
 * @param {object} opts
 * @param {string} [opts.range]      "today" | "yesterday" | "custom" | numeric preset ("7", "30", "90") | undefined
 * @param {string} [opts.customFrom] YYYY-MM-DD (only used when range === "custom")
 * @param {string} [opts.customTo]   YYYY-MM-DD (only used when range === "custom")
 * @param {number} [opts.days]       Numeric fallback when range is unset or numeric (default 7)
 * @returns {object} URLSearchParams-compatible plain object
 */
export function buildDateRangeParams({ range, customFrom, customTo, days } = {}) {
  const today = new Date();
  // Iter 219 (Aug 2026 · Bug — user reported: "when i select today
  // right now on the rep performance, i see no calls for reps, and
  // when i select yesterday, it shows the calls for that happened
  // today"). Root cause: this helper built the YYYY-MM-DD via
  // `d.toISOString().split("T")[0]`, which returns the UTC date. For
  // any user west of UTC (all US timezones) at any local time after
  // 4-8 PM, that returns TOMORROW's UTC calendar day instead of
  // today's LOCAL calendar day. Result: "Today" filter sent Tomorrow
  // → empty window; "Yesterday" filter sent Today → today's calls
  // appeared under the Yesterday label. Same class of bug as Iter 216
  // (Last Contact filter) — anchor to LOCAL calendar day, not UTC.
  const iso = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

  if (range === "today") {
    const t = iso(today);
    return { date_from: t, date_to: t };
  }
  if (range === "yesterday") {
    const y = new Date(today);
    y.setDate(y.getDate() - 1);
    const ys = iso(y);
    return { date_from: ys, date_to: ys };
  }
  if (range === "custom" && customFrom && customTo) {
    return { date_from: customFrom, date_to: customTo };
  }

  // Numeric preset or fallback. If range is a numeric string ("7", "30",
  // "90"), it wins; otherwise use the caller-supplied days (or default 7).
  const numeric = Number(range);
  const resolved = Number.isFinite(numeric) && numeric > 0 ? numeric : (days ?? 7);
  return { days: resolved };
}

/**
 * Convenience — same shape but as URLSearchParams for callers that
 * prefer to build the query string manually. Skips `undefined` values.
 */
export function buildDateRangeQueryString(opts) {
  const p = buildDateRangeParams(opts);
  const q = new URLSearchParams();
  Object.entries(p).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") q.set(k, String(v));
  });
  return q.toString();
}
